/** O Kanban (SPEC-OPP-020, DESIGN.md § 3.2).
 *
 * Três honestidades que a tela precisa manter:
 *
 * * **O atraso da projeção é visível.** O board é um read model alimentado por
 *   evento — assíncrono por desenho, que é o preço de a leitura não pagar seis
 *   junções. Esconder o atraso faria a interface mentir sobre o que ela mostra.
 * * **Coluna vazia convida, não fica em branco.** Um funil recém-criado é o
 *   primeiro contato da pessoa com o produto.
 * * **O cabeçalho da coluna é medidor, não rótulo.** Bolinha da etapa,
 *   contagem, valor em mono e o trilho com a fatia do funil: o que muda a
 *   decisão é quanto há em cada lugar, não o nome da etapa.
 */

import { Suspense, useEffect, useState } from "react";

import { PainelDaOportunidade } from "@/features/detalhe/painelSobDemanda";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";

import { useSessao } from "@/features/auth/SessaoProvider";
import { Botao } from "@/shared/ui/Botao";
import { Campo } from "@/shared/ui/Campo";
import { LimiteDeErro } from "@/shared/ui/LimiteDeErro";

import { useOffline } from "@/shared/hooks/useOffline";
import { useTelaEstreita } from "@/shared/hooks/useTelaEstreita";
import { EsqueletoDoFunil } from "@/shared/ui/Esqueleto";

import { BarraDeMetricas } from "./BarraDeMetricas";
import { CardDaOportunidade } from "./CardDaOportunidade";
import { FunilEmLista } from "./FunilEmLista";
import { contarFiltros, formatarDinheiro } from "./formato";
import { NovaOportunidade } from "./NovaOportunidade";
import { EVENTO_DE_CRIACAO, abreCriacao } from "./atalhoDeCriacao";
import { anunciosDoArrasto, saltarEntreEtapas } from "./tecladoDoArrasto";
import {
  comExtras,
  contemCard,
  useMaisDaColuna,
  useMoverOportunidade,
  usePerderOportunidade,
  useQuadro,
  type Aguardando,
} from "./useQuadro";
import { useTempoReal } from "./useTempoReal";
import { useEtiquetas } from "@/features/etiquetas/useEtiquetas";
import type { Coluna } from "./api";

export function QuadroDeOportunidades() {
  const [busca, setBusca] = useState("");
  /** Etiqueta escolhida no painel de filtros. Vazio = todas. */
  const [etiqueta, setEtiqueta] = useState("");
  /** Etapa em que a criação foi pedida. `null` = fechado; string vazia = pedido
   * pelo botão do topo, sem etapa preferida. */
  const [criandoEm, setCriandoEm] = useState<string | null>(null);
  const [filtrando, setFiltrando] = useState(false);
  const [soParadas, setSoParadas] = useState(false);
  /** O card aberto. Guarda o título junto para o painel ter o que mostrar
   * enquanto a consulta do detalhe não volta. */
  // `marcarCompromisso` viaja junto do id porque é a mesma decisão: quem clicou
  // em "Marcar compromisso" no card pediu a ficha **e** o formulário. Um estado
  // à parte poderia sobreviver ao fechamento e abrir o formulário na próxima
  // ficha, sem ninguém ter pedido.
  const [aberta, setAberta] = useState<{
    id: string;
    titulo: string;
    marcarCompromisso?: boolean;
  } | null>(null);
  /** O que ainda se espera da projeção, **uma entrada por card**.
   *
   * Enquanto há espera pendente, o board relê de segundo em segundo em vez de a
   * cada vinte. Com um slot só, mover dois cards em sequência descartava a
   * espera do primeiro e ele voltava sozinho para a coluna de origem — o
   * defeito de `d9b9a63` de volta para quem trabalha rápido.
   */
  const [esperas, setEsperas] = useState<Aguardando[]>([]);
  /** Qual etapa a lista de tela estreita mostra. `null` = a primeira. */
  const [etapaNaLista, setEtapaNaLista] = useState<string | null>(null);
  const telaEstreita = useTelaEstreita();
  const offline = useOffline();

  /** Registra (ou substitui) a espera daquele card. */
  function esperar(nova: Aguardando) {
    setEsperas((atuais) => [...atuais.filter((e) => e.id !== nova.id), nova]);
  }
  /** Card solto numa etapa de perda, esperando o motivo. */
  const [perdendo, setPerdendo] = useState<{ cardId: string; etapa: Coluna } | null>(null);
  /** Desiste da espera quando o servidor recusa o movimento.
   *
   * A espera é o que segura o card na coluna de destino enquanto a projeção
   * não alcança — e para um movimento recusado ela **nunca** vai alcançar.
   * Sem esta limpeza, o `onError` devolvia o cache ao estado anterior e o card
   * continuava na coluna nova, porque o `select` reaplicava o movimento a cada
   * render: a tela dizia "voltou para a etapa anterior" sobre um funil que
   * mostrava o contrário.
   *
   * Só limpa a espera **deste** card: mover outro card no intervalo registra
   * uma espera nova, e apagá-la aqui traria de volta o card que voltava
   * sozinho.
   */
  function desistirDaEspera(cardId: string) {
    setEsperas((atuais) => atuais.filter((espera) => espera.id !== cardId));
  }
  // `N` abre a criação (SPEC-FE-003). No documento, e não num elemento com
  // foco: o atalho precisa valer com o foco em qualquer lugar do board — que é
  // justamente o que o torna útil a quem não usa mouse.
  useEffect(() => {
    function aoTeclar(evento: KeyboardEvent) {
      if (!abreCriacao(evento)) return;
      // Sem isto, o `n` também é digitado no campo que receber o foco a
      // seguir — o formulário abriria já com uma letra no título.
      evento.preventDefault();
      setCriandoEm((atual) => atual ?? "");
    }

    // O "+" da navegação inferior chega por aqui: no celular não há teclado
    // para o atalho, e o formulário é estado deste componente.
    function aoPedirem() {
      setCriandoEm((atual) => atual ?? "");
    }
    window.addEventListener(EVENTO_DE_CRIACAO, aoPedirem);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      window.removeEventListener(EVENTO_DE_CRIACAO, aoPedirem);
    };
  }, []);

  const etiquetas = useEtiquetas();
  const emTempoReal = useTempoReal();
  const consulta = useQuadro({ busca, tag: etiqueta }, esperas, emTempoReal);
  const {
    extras,
    carregarMais,
    carregando: carregandoColuna,
  } = useMaisDaColuna({
    busca,
    tag: etiqueta,
  });
  const movimento = useMoverOportunidade();
  const perda = usePerderOportunidade();
  const { temPermissao } = useSessao();
  const podeFechar = temPermissao("opportunity:close");

  // Distância antes de virar arrasto: sem ela, o clique no título do card e no
  // menu `⋯` seria engolido pelo gesto, e o card ficaria só arrastável. O
  // teclado é sensor de primeira classe, não acréscimo — é o que mantém o
  // funil operável por quem não usa mouse (DESIGN.md § 5).
  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // `saltarEntreEtapas` no lugar do padrão: a biblioteca desloca 25 px por
    // tecla, e atravessar uma coluna de 300 px custaria treze toques. Ver
    // `tecladoDoArrasto.ts`.
    useSensor(KeyboardSensor, { coordinateGetter: saltarEntreEtapas }),
  );

  function aoSoltar(evento: DragEndEvent) {
    const cardId = String(evento.active.id);
    const destino = evento.over?.data.current?.coluna as Coluna | undefined;
    if (destino === undefined) return;

    const origem = (consulta.data?.columns ?? []).find((c) =>
      c.cards.some((card) => card.id === cardId),
    );
    if (origem === undefined || origem.stage_id === destino.stage_id) return;

    // Perder exige motivo, e o motivo não se adivinha do gesto. O card só sai
    // da coluna depois que a pessoa disser por quê.
    if (destino.stage_type === "lost") {
      setPerdendo({ cardId, etapa: destino });
      return;
    }

    // A espera é registrada **antes** da confirmação do servidor, de propósito:
    // o `onSettled` invalida a consulta assim que a resposta chega, e é essa
    // releitura — anterior à projeção — que devolvia o card para a coluna de
    // origem. Registrar depois deixaria justamente essa janela descoberta.
    esperar({ id: cardId, desde: Date.now(), paraEtapa: destino.stage_id });
    movimento.mutate(
      { cardId, paraEtapa: destino.stage_id },
      {
        onError: () => {
          desistirDaEspera(cardId);
        },
      },
    );
  }

  // As páginas extras entram aqui, e não no cache da consulta: o board relê
  // sozinho a cada ciclo e a releitura devolve a primeira página — gravadas no
  // cache, elas sumiriam na frente de quem acabou de pedir "carregar mais".
  const quadro = consulta.data === undefined ? undefined : comExtras(consulta.data, extras);
  const etapas = (quadro?.columns ?? []).map((coluna) => ({
    id: coluna.stage_id,
    nome: coluna.stage_name,
  }));

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <p className="flex items-center gap-2 font-texto text-micro uppercase tracking-wide text-texto-suave">
            {/* Marca de medição: dois traços de verde antes do nome do funil.
                É o único lugar do cabeçalho em que a cor da marca aparece, e
                ela aparece como instrumento — não como faixa decorativa. */}
            <span aria-hidden="true" className="inline-flex items-center gap-0.5">
              <span className="h-3 w-0.5 rounded-full bg-acao" />
              <span className="h-2 w-0.5 rounded-full bg-acao/50" />
            </span>
            {quadro?.pipeline?.name ?? "Funil"}
          </p>
          <h2 className="font-display text-display-md tracking-tight text-texto">Oportunidades</h2>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="relative flex items-center">
            <span className="sr-only">Buscar oportunidade</span>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 font-texto text-caption text-texto-tenue"
            >
              ⌕
            </span>
            <input
              data-guia="busca-do-funil"
              type="search"
              value={busca}
              onChange={(evento) => {
                setBusca(evento.target.value);
              }}
              placeholder="Buscar título ou empresa"
              className="h-10 w-[260px] rounded-md border border-borda bg-superficie pl-8 pr-3 font-texto text-body text-texto placeholder:text-texto-tenue focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva"
            />
          </label>
          <Botao
            variante={soParadas ? "primario" : "secundario"}
            aria-expanded={filtrando}
            onClick={() => {
              setFiltrando((antes) => !antes);
            }}
          >
            Filtros
            {contarFiltros(soParadas, etiqueta) > 0
              ? ` (${contarFiltros(soParadas, etiqueta)})`
              : ""}
          </Botao>
          {/* Some abaixo de 640px: a navegação inferior (2026-09-02) trouxe o
              "+" que cria oportunidade no celular, e este botão continuava
              nascendo do lado dele — dois "Nova oportunidade" visíveis ao
              mesmo tempo na mesma tela, o que `e2e/celular.spec.ts` acusava
              como dois elementos para o mesmo papel e nome acessível. O
              `Botao` não aceita `className` (é de propósito: a variante e o
              tamanho já decidem o visual), então quem esconde é o `span` em
              volta — `inline-flex` a partir de `sm` porque é o `display` que
              o próprio `Botao` já usa, para o botão ocupar exatamente o
              mesmo espaço que ocupava antes desta mudança. */}
          <span className="hidden sm:inline-flex">
            <Botao
              data-guia="nova-oportunidade"
              onClick={() => {
                setCriandoEm("");
              }}
            >
              <span aria-hidden="true">+</span> Nova oportunidade
              {/* O atalho fica escrito no botão: atalho que ninguém descobre não
                  economiza tempo de ninguém. Escondido do leitor de tela porque
                  ali ele viraria parte do nome da ação. */}
              <span aria-hidden="true" className="ml-2 text-caption text-texto-tenue">
                N
              </span>
            </Botao>
          </span>
        </div>
      </header>

      {filtrando && (
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-borda bg-superficie px-4 py-3">
          <label className="flex items-center gap-2 font-texto text-body text-texto">
            <input
              type="checkbox"
              checked={soParadas}
              onChange={(evento) => {
                setSoParadas(evento.target.checked);
              }}
              className="h-4 w-4 accent-[rgb(var(--tz-acao))]"
            />
            Só o que está parado
          </label>
          <label className="flex items-center gap-2 font-texto text-body text-texto">
            Etiqueta
            <select
              value={etiqueta}
              onChange={(evento) => {
                setEtiqueta(evento.target.value);
              }}
              className="h-10 rounded-md border border-borda bg-superficie px-3 font-texto text-body text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva"
            >
              {/* Pelo nome, e não pelo id: é o nome que o read model guarda
                  desnormalizado, e é por ele que o servidor filtra. */}
              <option value="">Todas</option>
              {(etiquetas.data ?? []).map((disponivel) => (
                <option key={disponivel.id} value={disponivel.name}>
                  {disponivel.name}
                </option>
              ))}
            </select>
          </label>
          {contarFiltros(soParadas, etiqueta) > 0 && (
            <button
              type="button"
              onClick={() => {
                setSoParadas(false);
                setEtiqueta("");
              }}
              className="font-texto text-caption text-texto-suave underline hover:text-texto"
            >
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Sem rede, a releitura periódica falha em silêncio e o funil continua
          com a cara de sempre — de minutos atrás (SPEC-FE-001 § 6). Quem move
          um card recebe "não foi possível mover", que soa a problema de
          servidor ou de permissão. O aviso troca isso por um diagnóstico que a
          pessoa consegue resolver. */}
      {offline && (
        <p
          role="status"
          className="flex items-center gap-2 rounded-lg border border-borda bg-ambar/10 px-4 py-3 font-texto text-caption text-texto"
        >
          <span aria-hidden="true" className="text-ambar">
            ●
          </span>
          Sem conexão. O funil abaixo é o último carregado, e mudanças não serão salvas até a rede
          voltar.
        </p>
      )}

      <BarraDeMetricas esperas={esperas} emTempoReal={emTempoReal} />

      {criandoEm !== null && (
        <NovaOportunidade
          etapas={etapas}
          etapaInicial={criandoEm === "" ? undefined : criandoEm}
          aoCriar={(id) => {
            esperar({ id, desde: Date.now() });
          }}
          aoFechar={() => {
            setCriandoEm(null);
          }}
        />
      )}

      {/* Só a criação: mover um card que já está na tela não precisa de aviso
          nenhum — a pessoa vê o card na coluna nova na hora. O aviso existe
          para o intervalo entre "Criar" e o card aparecer, que sem ele é lido
          como "não salvou". */}
      <Chegando
        criacoes={esperas.filter(
          (espera) =>
            espera.paraEtapa === undefined &&
            espera.paraResponsavel === undefined &&
            (quadro === undefined || !contemCard(quadro, espera.id)),
        )}
      />

      <AtrasoDaProjecao projetadoEm={quadro?.meta.projected_at ?? null} />

      {/* O leitor de tela precisa saber que o board mudou; a mudança acontece
          longe do foco, que continua no seletor do card. */}
      <p aria-live="polite" className="sr-only">
        {movimento.isPending ? "Movendo oportunidade." : ""}
        {movimento.isError ? "Não foi possível mover a oportunidade." : ""}
      </p>

      {movimento.isError && (
        <p role="alert" className="font-texto text-body text-carmim">
          Não foi possível mover a oportunidade. Ela voltou para a etapa anterior.
        </p>
      )}

      {consulta.isPending && <EsqueletoDoFunil />}

      {consulta.isError && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-borda bg-superficie px-4 py-3"
        >
          <p className="font-texto text-body text-carmim">Não foi possível carregar o funil.</p>
          {/* Retry no lugar de "tente de novo em instantes" (SPEC-FE-001 § 6):
              mandar a pessoa recarregar a página é pedir que ela faça o que o
              botão faz — e recarregar perde o filtro que ela tinha digitado. */}
          <Botao
            tamanho="sm"
            variante="secundario"
            carregando={consulta.isFetching}
            onClick={() => void consulta.refetch()}
          >
            Tentar de novo
          </Botao>
        </div>
      )}

      {quadro !== undefined && quadro.columns.length === 0 && (
        <Estado texto="Este tenant ainda não tem um funil configurado." alerta />
      )}

      {quadro !== undefined && quadro.columns.length > 0 && telaEstreita && (
        <FunilEmLista
          colunas={quadro.columns}
          etapaAtiva={etapaNaLista ?? quadro.columns[0]?.stage_id ?? ""}
          aoTrocarEtapa={setEtapaNaLista}
          etapas={etapas}
          movendoCard={movimento.isPending ? movimento.variables.cardId : undefined}
          carregandoColuna={carregandoColuna}
          aoCarregarMais={(coluna) => {
            if (coluna.next_cursor === null) return;
            void carregarMais(coluna.stage_id, coluna.next_cursor);
          }}
          aoMover={(cardId, paraEtapa) => {
            const destino = quadro.columns.find((c) => c.stage_id === paraEtapa);
            if (destino !== undefined && destino.stage_type === "lost") {
              setPerdendo({ cardId, etapa: destino });
              return;
            }
            esperar({ id: cardId, desde: Date.now(), paraEtapa });
            movimento.mutate(
              { cardId, paraEtapa },
              {
                onError: () => {
                  desistirDaEspera(cardId);
                },
              },
            );
          }}
          aoAbrir={(id, titulo, marcarCompromisso) => {
            setAberta({ id, titulo, marcarCompromisso });
          }}
          aoCriarNaEtapa={setCriandoEm}
        />
      )}

      {quadro !== undefined && quadro.columns.length > 0 && !telaEstreita && (
        <DndContext
          sensors={sensores}
          onDragEnd={aoSoltar}
          // Os anúncios da biblioteca são em inglês. Um board em português que
          // fala "Draggable item was moved" para o leitor de tela é pior que o
          // silêncio (ADR-0016).
          accessibility={{ announcements: anunciosDoArrasto(quadro) }}
        >
          <div
            data-guia="colunas-do-funil"
            className="flex items-start gap-4 overflow-x-auto pb-4 [scrollbar-color:rgb(var(--tz-borda))_transparent] [scrollbar-width:thin]"
          >
            {quadro.columns.map((coluna) => (
              <ColunaDoFunil
                key={coluna.stage_id}
                coluna={coluna}
                podeReceber={coluna.stage_type !== "lost" || podeFechar}
                fatiaDoFunil={fatiaDoFunil(coluna, quadro.columns)}
                etapas={etapas}
                soParadas={soParadas}
                carregandoMais={carregandoColuna === coluna.stage_id}
                aoCarregarMais={() => {
                  if (coluna.next_cursor === null) return;
                  void carregarMais(coluna.stage_id, coluna.next_cursor);
                }}
                aoMover={(cardId, paraEtapa) => {
                  // O menu segue as mesmas duas regras do arrasto, e por muito
                  // tempo não seguiu nenhuma das duas.
                  //
                  // Ele mandava `PATCH /stage` puro: escolher "Perdido" no
                  // menu recebia 422 do domínio ("perda exige motivo") e o card
                  // voltava para a coluna de origem sem explicar nada. Como o
                  // menu é o caminho de teclado (ADR-0016), **quem não usa
                  // mouse não conseguia marcar nada como perdido** — o gesto
                  // que sobrou era o único que funcionava.
                  const destino = quadro.columns.find((c) => c.stage_id === paraEtapa);
                  if (destino !== undefined && destino.stage_type === "lost") {
                    setPerdendo({ cardId, etapa: destino });
                    return;
                  }
                  // E registrava o movimento sem declarar o que espera ver, o
                  // que deixava a releitura anterior à projeção devolver o card
                  // à origem — o defeito de `d9b9a63`, corrigido no arrasto e
                  // não aqui.
                  esperar({ id: cardId, desde: Date.now(), paraEtapa });
                  movimento.mutate(
                    { cardId, paraEtapa },
                    {
                      onError: () => {
                        desistirDaEspera(cardId);
                      },
                    },
                  );
                }}
                aoAbrir={(cardId, titulo, marcarCompromisso) => {
                  setAberta({ id: cardId, titulo, marcarCompromisso });
                }}
                aoCriarNaEtapa={() => {
                  setCriandoEm(coluna.stage_id);
                }}
                movendoCard={movimento.isPending ? movimento.variables.cardId : undefined}
              />
            ))}
          </div>
        </DndContext>
      )}

      {perdendo !== null && (
        <MotivoDaPerda
          etapa={perdendo.etapa.stage_name}
          salvando={perda.isPending}
          erro={perda.isError}
          aoConfirmar={(motivo, observacao) => {
            // Mesma espera do movimento comum: perder também passa pelo
            // projetor, e sem ela o card volta para a coluna de origem.
            esperar({
              id: perdendo.cardId,
              desde: Date.now(),
              paraEtapa: perdendo.etapa.stage_id,
              perdida: true,
            });
            perda.mutate(
              {
                cardId: perdendo.cardId,
                paraEtapa: perdendo.etapa.stage_id,
                motivo,
                observacao,
              },
              {
                onSuccess: () => {
                  setPerdendo(null);
                },
                // O diálogo diz "O card continua onde estava" — e sem desistir
                // da espera o board o mostrava na coluna de perda, contradizendo
                // a própria mensagem.
                onError: () => {
                  desistirDaEspera(perdendo.cardId);
                },
              },
            );
          }}
          aoCancelar={() => {
            // Cancelar depois de uma tentativa recusada fechava o diálogo e
            // deixava o card na coluna de perda para sempre: a mensagem sumia
            // junto, e não sobrava nada na tela dizendo que a perda não valeu.
            desistirDaEspera(perdendo.cardId);
            perda.reset();
            setPerdendo(null);
          }}
        />
      )}

      {/* O painel é a região que já derrubou a tela inteira: ele leu
          `stage.name` de uma etapa ausente e o CRM sumiu. Com limite próprio,
          o pior caso passa a ser uma ficha que não abre — o funil atrás dela
          continua de pé, e "Fechar" devolve a pessoa a ele. */}
      {aberta !== null && (
        <LimiteDeErro
          area="A ficha da oportunidade"
          acaoSecundaria={{
            rotulo: "Fechar",
            aoAcionar: () => {
              setAberta(null);
            },
          }}
        >
          <Suspense
            fallback={
              <p role="status" className="p-6 font-texto text-body text-texto-suave">
                Abrindo o negócio…
              </p>
            }
          >
            <PainelDaOportunidade
              opportunityId={aberta.id}
              tituloProvisorio={aberta.titulo}
              marcarCompromisso={aberta.marcarCompromisso === true}
              // O card no board mostra o responsável, e o board é projeção: sem
              // esta espera o nome antigo permanecia até a releitura de vinte
              // segundos, e quem acabou de trocar concluía que não salvou.
              aoTrocarResponsavel={(ownerId) => {
                esperar({ id: aberta.id, desde: Date.now(), paraResponsavel: ownerId });
              }}
              aoFechar={() => {
                setAberta(null);
              }}
            />
          </Suspense>
        </LimiteDeErro>
      )}
    </section>
  );
}

/** Quanto do funil está parado nesta etapa, de 0 a 1.
 *
 * Sobre o **valor**, e não sobre a contagem: cinco negociações de mil reais e
 * uma de cem mil não pesam igual, e o trilho existe para mostrar onde o
 * dinheiro está. Funil zerado devolve 0 para todas — nenhuma barra acesa é a
 * leitura correta de "não há nada em jogo ainda".
 */
function fatiaDoFunil(coluna: Coluna, todas: readonly Coluna[]): number {
  const total = todas.reduce((soma, outra) => soma + outra.totals.value_cents, 0);
  return total === 0 ? 0 : coluna.totals.value_cents / total;
}

/** O nome da etapa para o anúncio do leitor de tela.
 *
 * Vem do `data` que a coluna registra no `useDroppable`, e não do `id`: o id é
 * um UUID, e anunciar "Movido para 018f-…" não ajuda ninguém. */

/** O motivo da perda, pedido antes de mover.
 *
 * `SPEC-OPP-040`: perder exige `reason_code`, e é o que permite descobrir
 * depois por que se perde. O board mandava `PATCH /stage` puro e a perda
 * falhava com `422` — a rota `/lose` sempre existiu e a tela não a usava.
 *
 * O campo é livre porque o domínio não define vocabulário de motivos. Inventar
 * uma lista aqui seria decidir, na tela, uma taxonomia que o produto ainda não
 * decidiu — e listas de motivo mal escolhidas viram "Outro" em 80% dos casos.
 */
function MotivoDaPerda({
  etapa,
  salvando,
  erro,
  aoConfirmar,
  aoCancelar,
}: {
  etapa: string;
  salvando: boolean;
  erro: boolean;
  aoConfirmar: (motivo: string, observacao?: string) => void;
  aoCancelar: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [observacao, setObservacao] = useState("");
  const pode = motivo.trim() !== "" && !salvando;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-6">
      <div aria-hidden="true" onClick={aoCancelar} className="absolute inset-0 bg-texto/25" />

      <form
        role="dialog"
        aria-modal="true"
        aria-label={`Mover para ${etapa}`}
        onSubmit={(evento) => {
          evento.preventDefault();
          if (pode) aoConfirmar(motivo.trim(), observacao);
        }}
        className="relative flex w-full max-w-[420px] flex-col gap-4 rounded-lg border border-borda bg-superficie p-5 shadow-e3"
      >
        <div className="flex flex-col gap-1">
          <h2 className="font-display text-title text-texto">Por que perdeu?</h2>
          <p className="font-texto text-caption text-texto-suave">
            O motivo fica na oportunidade e no relatório de perdas. É o que permite descobrir depois
            onde o funil vaza.
          </p>
        </div>

        <Campo
          rotulo="Motivo"
          value={motivo}
          maxLength={60}
          onChange={(evento) => {
            setMotivo(evento.target.value);
          }}
          placeholder="Preço, prazo, concorrente…"
          required
          autoFocus
        />

        <Campo
          rotulo="Detalhe (opcional)"
          value={observacao}
          onChange={(evento) => {
            setObservacao(evento.target.value);
          }}
          placeholder="Concorrente entrou 20% abaixo."
        />

        {erro && (
          <p role="alert" className="font-texto text-caption text-carmim">
            Não foi possível registrar a perda. O card continua onde estava.
          </p>
        )}

        <div className="flex gap-2">
          <Botao type="submit" carregando={salvando} disabled={!pode}>
            Marcar como perdida
          </Botao>
          <Botao type="button" variante="fantasma" onClick={aoCancelar}>
            Cancelar
          </Botao>
        </div>
      </form>
    </div>
  );
}

interface PropsDaColuna {
  coluna: Coluna;
  /** Se esta coluna aceita um card solto nela. Etapa de perda só aceita de quem
   * pode fechar — oferecer o destino a quem receberia `403` é convidar para
   * uma ação que não existe. */
  podeReceber: boolean;
  etapas: readonly { id: string; nome: string }[];
  aoMover: (cardId: string, paraEtapa: string) => void;
  aoAbrir: (cardId: string, titulo: string, marcarCompromisso?: boolean) => void;
  aoCriarNaEtapa: () => void;
  movendoCard?: string;
  /** Fatia desta coluna no valor total do funil, de 0 a 1. */
  fatiaDoFunil: number;
  soParadas: boolean;
  aoCarregarMais: () => void;
  carregandoMais: boolean;
}

function ColunaDoFunil({
  coluna,
  podeReceber,
  etapas,
  aoMover,
  aoAbrir,
  aoCriarNaEtapa,
  movendoCard,
  fatiaDoFunil,
  soParadas,
  aoCarregarMais,
  carregandoMais,
}: PropsDaColuna) {
  const moeda = coluna.cards[0]?.currency ?? "BRL";
  const corDaEtapa = coluna.stage_color ?? "rgb(var(--tz-acao))";
  // A coluna inteira é o alvo, e não a lista de cards: soltar na área vazia
  // embaixo do último card é o gesto natural, e uma zona que termina onde os
  // cards terminam faz a pessoa mirar.
  const { setNodeRef, isOver: sobreposta } = useDroppable({
    id: coluna.stage_id,
    disabled: !podeReceber,
    data: { coluna },
  });
  // O filtro é aplicado sobre os cards já carregados, e não na consulta: ele
  // existe para reduzir o que a pessoa lê agora, não para pedir outra página.
  // Os totais do cabeçalho continuam sendo os da coluna inteira, porque é
  // deles que sai a decisão de onde mexer.
  const cards = soParadas ? coluna.cards.filter((card) => card.is_stale) : coluna.cards;
  const vazia = cards.length === 0;

  return (
    <section
      ref={setNodeRef}
      aria-label={`Etapa ${coluna.stage_name}`}
      className={[
        "flex w-[300px] shrink-0 flex-col gap-3 rounded-lg",
        // Contorno tracejado verde no destino (DESIGN.md § 3.1). Só enquanto o
        // card paira: borda permanente em cinco colunas seria ruído.
        sobreposta ? "outline-dashed outline-2 outline-offset-4 outline-acao" : "",
      ].join(" ")}
    >
      {/* Cabeçalho fixo: rolar a coluna não pode fazer a pessoa perder de vista
          quanto tem em jogo nela.

          O valor é o elemento maior, em mono: é o número que se compara
          verticalmente entre colunas, e comparar dígitos exige que eles se
          alinhem. O nome da etapa é sobrelinha — quem olha o funil já sabe
          onde está. */}
      <header className="sticky top-0 z-10 flex flex-col gap-3 rounded-lg border border-borda bg-superficie px-4 py-3.5 shadow-e1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {/* A bolinha carrega a `stage_color` que a API já devolvia e que
                a tela desperdiçava numa borda de 1px. */}
            <span
              aria-hidden="true"
              className="h-[7px] w-[7px] shrink-0 rounded-full"
              style={{ backgroundColor: corDaEtapa }}
            />
            {/* Continua `h3`: a sobrelinha é tratamento visual, e trocar por
                `p` tiraria a estrutura de cabeçalhos que o leitor de tela usa
                para saltar entre as colunas do funil. */}
            <h3 className="truncate font-texto text-micro font-bold uppercase tracking-wide text-texto">
              {coluna.stage_name}
            </h3>
          </div>
          <span
            className={[
              "shrink-0 rounded-full px-2 py-0.5 font-dados text-micro tabular-nums",
              coluna.totals.count === 0 ? "bg-canvas text-texto-tenue" : "bg-acao/10 text-acao",
            ].join(" ")}
          >
            {coluna.totals.count}
          </span>
        </div>

        <p
          className={[
            "font-dados text-title tabular-nums",
            coluna.totals.value_cents === 0 ? "text-texto-tenue" : "text-texto",
          ].join(" ")}
        >
          {formatarDinheiro(coluna.totals.value_cents, moeda)}
          {/* O ponderado só aparece quando difere do total: repetir o mesmo
              número duas vezes gasta linha e não informa nada. */}
          {coluna.totals.weighted_value_cents !== coluna.totals.value_cents && (
            <span className="ml-2 font-dados text-micro tabular-nums text-texto-tenue">
              ponderado {formatarDinheiro(coluna.totals.weighted_value_cents, moeda)}
            </span>
          )}
        </p>

        {/* Trilho da etapa. A espessura preenchida é a fatia desta coluna no
            valor total do funil — informação, não enfeite: mostra onde o
            dinheiro está parado antes de qualquer leitura de número. */}
        <div className="h-[3px] w-full overflow-hidden rounded-full bg-canvas" aria-hidden="true">
          <div
            className="h-full rounded-full transition-[width] duration-500"
            style={{
              width: `${String(Math.round(fatiaDoFunil * 100))}%`,
              backgroundColor: corDaEtapa,
            }}
          />
        </div>
      </header>

      {vazia ? (
        /* "Coluna vazia mostra convite de ação, nunca espaço vazio"
           (DESIGN.md § 3.2). */
        <p className="rounded-lg border border-dashed border-borda px-4 py-6 text-center font-texto text-caption leading-relaxed text-texto-tenue">
          {soParadas && coluna.cards.length > 0
            ? "Nada parado nesta etapa."
            : "Arraste um card para cá — ou crie uma oportunidade nesta etapa."}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {cards.map((card) => (
            <li key={card.id}>
              <CardDaOportunidade
                card={card}
                etapas={etapas}
                etapaAtual={coluna.stage_id}
                movendo={movendoCard === card.id}
                aoMover={(paraEtapa) => {
                  aoMover(card.id, paraEtapa);
                }}
                aoAbrir={(marcarCompromisso) => {
                  aoAbrir(card.id, card.title, marcarCompromisso);
                }}
              />
            </li>
          ))}
        </ul>
      )}

      {/* O resto da coluna (SPEC-OPP-020 § 3).
          Só aparece quando há mais: o botão é a única pista de que a lista não
          termina onde o olho vê. Antes de existir, o total no cabeçalho dizia
          63 sobre uma lista de 50 e nada explicava a diferença.

          Escondido enquanto "só paradas" está ligado: ali a lista já está
          filtrada sobre o que foi carregado, e pedir a próxima página traria
          cards que o filtro esconderia — um botão que parece não fazer nada. */}
      {coluna.next_cursor !== null && !soParadas && (
        <button
          type="button"
          onClick={aoCarregarMais}
          disabled={carregandoMais}
          className="rounded-md border border-borda px-2.5 py-2 font-texto text-caption font-semibold text-texto-suave hover:bg-canvas hover:text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva disabled:opacity-60"
        >
          {carregandoMais
            ? "Carregando…"
            : `Carregar mais (${coluna.totals.count - coluna.cards.length} restantes)`}
        </button>
      )}

      {/* Criar já na etapa certa poupa a escolha que o formulário faria
          repetir — quem clica aqui já disse onde quer o card. */}
      <button
        type="button"
        onClick={aoCriarNaEtapa}
        className="rounded-md px-2.5 py-2 text-left font-texto text-caption font-semibold text-texto-suave hover:bg-canvas hover:text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva"
      >
        <span aria-hidden="true">+ </span>
        Adicionar oportunidade
      </button>
    </section>
  );
}

/** Enquanto a oportunidade recém-criada não chega no board.
 *
 * O board é read model: a escrita já terminou, mas o card só existe na tela
 * quando o projetor o processa. Sem este aviso, o intervalo entre "Criar" e o
 * card aparecer é lido como "não salvou" — e a pessoa cadastra de novo.
 *
 * Some assim que o card chega. Não vira mensagem de sucesso: o card na coluna
 * já é a confirmação, e um aviso a mais depois disso é ruído.
 */
function Chegando({ criacoes }: { criacoes: readonly Aguardando[] }) {
  if (criacoes.length === 0) return null;

  return (
    <p role="status" className="font-texto text-caption text-texto-suave">
      {criacoes.length === 1
        ? "Oportunidade registrada. Ela aparece no funil em instantes."
        : `${String(criacoes.length)} oportunidades registradas. Elas aparecem no funil em instantes.`}
    </p>
  );
}

/** O board mostra o estado projetado, não o estado agora. Quanto tempo faz é
 * informação de confiança: sem ela, um card que ainda não apareceu vira "o
 * sistema perdeu meu registro". */
function AtrasoDaProjecao({ projetadoEm }: { projetadoEm: string | null }) {
  if (projetadoEm === null) return null;

  const atrasoS = Math.max(0, Math.round((Date.now() - new Date(projetadoEm).getTime()) / 1000));
  if (atrasoS < 60) return null;

  const minutos = Math.round(atrasoS / 60);
  return (
    <p className="font-texto text-caption text-texto-suave">
      Projeção atualizada há {minutos} {minutos === 1 ? "minuto" : "minutos"}. Oportunidades criadas
      agora podem levar alguns instantes para aparecer.
    </p>
  );
}

function Estado({ texto, alerta = false }: { texto: string; alerta?: boolean }) {
  return (
    <p
      role={alerta ? "alert" : "status"}
      className={`font-texto text-body ${alerta ? "text-carmim" : "text-texto-suave"}`}
    >
      {texto}
    </p>
  );
}
